/** Account-owned break recovery for the browser in-call shift panel. */
import { useCallback, useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import { timeoutPromise } from '../../base/util/timeoutPromise';
import { getDefaultInterpreterLanguages } from '../../interpreter-queue/interpreterLanguages';
import { queueService } from '../../interpreter-queue/InterpreterQueueService';
import { apiClient } from '../../shared/api-client';
import { getPersistentItem, getPersistentJson } from '../../vrs-auth/storage';
const timestamp = z.string().refine(value => Number.isFinite(Date.parse(value)));
const breakSchema = z.object({
    id: z.string().min(1).max(120), started_at: timestamp, ended_at: timestamp.nullable(),
    duration_minutes: z.number().finite().nonnegative().nullish(),
    metadata: z.object({ restoreQueueAvailability: z.boolean().optional() }).nullish()
}).refine(value => !value.ended_at || Date.parse(value.ended_at) >= Date.parse(value.started_at));
const breaksSchema = z.object({ breaks: z.array(breakSchema), breakMinutesPerHour: z.number().finite().nonnegative() });
export function shiftAccountKey() {
    const role = getPersistentItem('vrs_user_role');
    const profile = getPersistentJson('vrs_user_info');
    return role === 'interpreter' && typeof profile?.id === 'string' && profile.id
        ? JSON.stringify([role, profile.id]) : null;
}
/** Bound the UI even when a transport fails to settle after abort. */
export async function shiftRequest(request, signal) {
    let onAbort = () => { };
    const cancelled = new Promise((_resolve, reject) => {
        onAbort = () => reject(new Error('Shift request cancelled'));
        signal.addEventListener('abort', onAbort, { once: true });
        if (signal.aborted)
            onAbort();
    });
    try {
        return await timeoutPromise(Promise.race([request, cancelled]), 15000);
    }
    finally {
        signal.removeEventListener('abort', onAbort);
    }
}
export function computeBreakLine(breaks, now, rate) {
    const active = breaks.find(item => !item.ended_at);
    if (active)
        return { active, text: `On break — ${Math.max(0, Math.round((now - Date.parse(active.started_at)) / 60000))}m` };
    const shiftStart = Number(getPersistentItem('vrs_shift_started_at') || 0);
    if (!Number.isFinite(shiftStart) || shiftStart <= 0)
        return { active, text: 'Break bank unavailable off shift' };
    if (rate === 0)
        return { active, text: '0m break banked' };
    const minutes = Math.max(0, (now - shiftStart) / 60000);
    const taken = breaks.reduce((sum, item) => !item.ended_at || new Date(item.started_at).toDateString() !== new Date(now).toDateString()
        ? sum : sum + Math.max(0, item.duration_minutes ?? Math.round((Date.parse(item.ended_at) - Date.parse(item.started_at)) / 60000)), 0);
    const banked = Math.max(0, Math.floor(minutes / 60) * rate - taken);
    return { active, text: banked > 0 ? `Break available: ${banked}m banked` : `Next break in ${Math.max(1, Math.ceil(60 - minutes % 60))}m` };
}
export default function useShiftBreaks(open) {
    const account = shiftAccountKey();
    const mounted = useRef(true);
    const load = useRef(null);
    const revision = useRef(0);
    const change = useRef(null);
    const [state, setState] = useState({ account, breaks: [], rate: 10, status: 'loading' });
    const [busy, setBusy] = useState(false);
    const [unconfirmed, setUnconfirmed] = useState(false);
    const [note, setNote] = useState('');
    const [now, setNow] = useState(Date.now());
    const [availability, setAvailability] = useState({ pending: false, failed: false, text: '' });
    const availabilityTarget = useRef(null);
    const availabilityTimer = useRef(null);
    const isCurrent = () => mounted.current && Boolean(account) && shiftAccountKey() === account;
    const current = state.account === account;
    const status = current ? state.status : 'loading';
    const line = computeBreakLine(current ? state.breaks : [], now, state.rate);
    const clearAvailabilityTimer = useCallback(() => {
        if (availabilityTimer.current)
            clearTimeout(availabilityTimer.current);
        availabilityTimer.current = null;
    }, []);
    const requestAvailability = useCallback((target) => {
        if (!isCurrent() || availabilityTimer.current)
            return;
        availabilityTarget.current = target;
        setAvailability({ pending: true, failed: false, text: 'Confirming your status…' });
        availabilityTimer.current = setTimeout(() => {
            availabilityTimer.current = null;
            if (isCurrent())
                setAvailability({ pending: false, failed: true,
                    text: 'Your status could not be confirmed. Try again.' });
        }, 15000);
        const profile = getPersistentJson('vrs_user_info');
        const savedLanguages = profile?.languages;
        const languages = Array.isArray(savedLanguages) && savedLanguages.length > 0
            && savedLanguages.every((value) => typeof value === 'string' && value.trim().length > 0)
            ? savedLanguages : getDefaultInterpreterLanguages();
        try {
            queueService.updateInterpreterStatus(target ? 'active' : 'inactive', typeof profile?.name === 'string' ? profile.name : undefined, target ? languages : []);
        }
        catch {
            clearAvailabilityTimer();
            if (isCurrent())
                setAvailability({ pending: false, failed: true,
                    text: 'Your status could not be sent. Try again.' });
        }
    }, [account, clearAvailabilityTimer]);
    const loadBreaks = useCallback(async () => {
        load.current?.abort();
        const controller = new AbortController();
        load.current = controller;
        const readRevision = revision.current;
        const owns = () => isCurrent() && load.current === controller && !controller.signal.aborted && readRevision === revision.current;
        setState(previous => ({ account, breaks: [], rate: previous.account === account ? previous.rate : 10, status: 'loading' }));
        try {
            if (!account)
                throw new Error('Interpreter profile unavailable');
            const response = await shiftRequest(apiClient.get('/api/interpreter/breaks?limit=10', { signal: controller.signal, retryAuth: false }), controller.signal);
            const parsed = breaksSchema.safeParse(response.data);
            if (response.error || response.status < 200 || response.status >= 300 || !parsed.success)
                throw new Error('Break status unavailable');
            if (owns())
                setState({ account, breaks: parsed.data.breaks, rate: parsed.data.breakMinutesPerHour, status: 'ready' });
        }
        catch {
            if ((owns() || !account && load.current === controller) && mounted.current && !controller.signal.aborted) {
                setState(previous => ({ ...previous, status: 'failed' }));
            }
        }
        finally {
            controller.abort();
        }
    }, [account]);
    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);
    useEffect(() => {
        setBusy(false);
        setUnconfirmed(false);
        setNote('');
        setAvailability({ pending: false, failed: false, text: '' });
        availabilityTarget.current = null;
        const onStatus = (data) => {
            if (!isCurrent() || availabilityTarget.current === null || !data || typeof data !== 'object')
                return;
            const payload = data;
            if (typeof payload.status !== 'string')
                return;
            const value = payload.status.toLowerCase();
            const active = ['active', 'available'].includes(value);
            if (!active && !['inactive', 'offline', 'unavailable', 'online', 'busy'].includes(value))
                return;
            clearAvailabilityTimer();
            const mismatch = active !== availabilityTarget.current;
            setAvailability({ pending: false, failed: mismatch, text: mismatch
                    ? 'The queue confirmed a different status.'
                    : active ? 'You are Available again.' : 'You are Paused.' });
        };
        const onConnection = (data) => {
            if (!isCurrent() || availabilityTarget.current === null || !data || typeof data !== 'object'
                || data.connected !== false)
                return;
            clearAvailabilityTimer();
            setAvailability({ pending: false, failed: true, text: 'Queue connection lost.' });
        };
        queueService.on('statusUpdated', onStatus);
        queueService.on('connection', onConnection);
        const timer = setInterval(() => setNow(Date.now()), 30000);
        return () => {
            change.current?.controller.abort();
            change.current = null;
            clearAvailabilityTimer();
            clearInterval(timer);
            queueService.off('statusUpdated', onStatus);
            queueService.off('connection', onConnection);
        };
    }, [account, clearAvailabilityTimer]);
    useEffect(() => {
        if (open)
            void loadBreaks();
        return () => { load.current?.abort(); load.current = null; };
    }, [open, loadBreaks]);
    const ownsChange = (operation) => mounted.current && change.current === operation && shiftAccountKey() === operation.account;
    const confirm = (operation, row, rate) => {
        if (!ownsChange(operation))
            return;
        revision.current++;
        setState(previous => ({ account: operation.account, breaks: [row, ...previous.breaks.filter(item => item.id !== row.id)],
            rate: rate ?? previous.rate, status: 'ready' }));
        change.current = null;
        setUnconfirmed(false);
        if (!operation.ending) {
            setNote('Break started.');
            requestAvailability(false);
        }
        else if (operation.restoreAvailability) {
            setNote('Break ended.');
            requestAvailability(true);
        }
        else
            setNote('Break ended.');
    };
    const checkBreak = async () => {
        const operation = change.current;
        if (!operation || operation.phase !== 'unconfirmed' || !ownsChange(operation))
            return;
        operation.phase = 'checking';
        operation.controller = new AbortController();
        setBusy(true);
        try {
            const response = await shiftRequest(apiClient.get('/api/interpreter/breaks?limit=10', { signal: operation.controller.signal, retryAuth: false }), operation.controller.signal);
            const parsed = breaksSchema.safeParse(response.data);
            if (response.error || response.status < 200 || response.status >= 300 || !parsed.success)
                throw new Error('Break status unavailable');
            if (!ownsChange(operation))
                return;
            const row = operation.ending ? parsed.data.breaks.find(item => item.id === operation.id && item.ended_at)
                : parsed.data.breaks.find(item => !item.ended_at);
            if (row)
                confirm(operation, row, parsed.data.breakMinutesPerHour);
            else
                setNote('Break change not confirmed.');
        }
        catch {
            if (ownsChange(operation))
                setNote('Couldn\'t check break status. Try again.');
        }
        finally {
            operation.controller.abort();
            if (mounted.current && shiftAccountKey() === operation.account && (!change.current || change.current === operation))
                setBusy(false);
            if (change.current === operation)
                operation.phase = 'unconfirmed';
        }
    };
    const toggleBreak = async () => {
        if (!account || !isCurrent() || change.current || availabilityTimer.current || status !== 'ready')
            return;
        const operation = { account, ending: Boolean(line.active), id: line.active?.id,
            restoreAvailability: line.active?.metadata?.restoreQueueAvailability === true,
            controller: new AbortController(), phase: 'pending' };
        change.current = operation;
        setBusy(true);
        setNote('');
        try {
            const response = await shiftRequest(apiClient.post(operation.ending
                ? `/api/interpreter/breaks/${encodeURIComponent(operation.id || '')}/end` : '/api/interpreter/breaks/start', operation.ending ? {} : { breakType: 'general', paid: false }, { signal: operation.controller.signal, retryAuth: false }), operation.controller.signal);
            if ([400, 401, 403].includes(response.status)) {
                if (ownsChange(operation)) {
                    change.current = null;
                    setUnconfirmed(false);
                    setState(previous => ({ ...previous, status: 'failed' }));
                    setNote('Break change was not accepted.');
                }
                return;
            }
            const parsed = z.object({ break: breakSchema }).safeParse(response.data);
            if (response.error || response.status < 200 || response.status >= 300 || !parsed.success || (operation.ending
                ? parsed.data.break.id !== operation.id || !parsed.data.break.ended_at : Boolean(parsed.data.break.ended_at))) {
                throw new Error('Break change unconfirmed');
            }
            confirm(operation, parsed.data.break);
        }
        catch {
            if (ownsChange(operation)) {
                operation.phase = 'unconfirmed';
                setUnconfirmed(true);
                setNote('Break change not confirmed.');
            }
        }
        finally {
            operation.controller.abort();
            if (mounted.current && shiftAccountKey() === operation.account && (!change.current || change.current === operation))
                setBusy(false);
        }
    };
    return { account, line, status, busy, unconfirmed, note, availability, loadBreaks, toggleBreak, checkBreak,
        retryAvailability: () => { if (availabilityTarget.current !== null && !change.current)
            requestAvailability(availabilityTarget.current); } };
}
